<script lang="ts" setup>
import { reactive, ref, watch } from 'vue'
import type { NewsData } from '~/composables/news/interface'
import SidebarCategory from '~/components/desktop/pages/news/sidebar-category.vue'
import NewsHighLight from '~/components/desktop/pages/news/news-highlight.vue'
import NewsHotMatches from '~/components/desktop/pages/news/news-hot-matches.vue'
import SeoHeadings from '~/components/common/seo-headings.vue'
import SliderNavigation from '~/components/common/slider-navigation.vue'
import NewsCategory from '~/components/desktop/pages/news/news-category.vue'
import NewsWeekly from '~/components/desktop/pages/news/news-weekly.vue'
import { useNews } from '~/composables/news/use-news'
import { sideBarCategories, listCategory, SLUG_CATEGORY_NEWS } from '~/resources/news'
import { SLUG_NEWS } from '~/constants'
import { useSwiper } from '~/composables/common/use-swiper'

const { modules, SLIDERS_PER_VIEW, SPACE_BETWEEN_SWIPER } = useSwiper()
const { fetchAllCategoryData, categorySlugCurrent } = useNews()
const dataNews = reactive<NewsData[]>([])
const limit: number = 20
fetchAllCategoryData(dataNews, 0, limit, SLUG_CATEGORY_NEWS.SOI_KEO)

interface SliderState {
  isBlurredRight: boolean
  isBlurredLeft: boolean
}

const sliderState = reactive<SliderState>({
  isBlurredRight: true,
  isBlurredLeft: false
})

const categoryName = ref('')
const isCategoryPage = ref(false)
watch(
  () => categorySlugCurrent.value,
  value => {
    if (value && value !== sideBarCategories[0].slug) {
      isCategoryPage.value = true
      const currentCategory = listCategory.find(item => item.slug === categorySlugCurrent.value)
      categoryName.value = currentCategory?.name ? currentCategory.name.toLowerCase() : ''
    } else {
      isCategoryPage.value = false
    }
  },
  { immediate: true }
)
</script>
<template>
  <div class="news-content">
    <div class="page-title">
      <span class="icon-news-home"></span>
      <NuxtLink to="/">Trang chủ</NuxtLink> >
      <NuxtLink :class="[{ 'current-slug': !isCategoryPage }]" :to="`${SLUG_NEWS.PAGE}`">Tin tức</NuxtLink>
      <NuxtLink v-if="isCategoryPage" class="current-slug text-capitalize" :to="`tin-tuc?cat=${categorySlugCurrent}`"
        >> {{ categoryName }}
      </NuxtLink>
    </div>

    <div class="row">
      <div class="col-lg-3 news-content__left">
        <SidebarCategory :categorySlugCurrent="categorySlugCurrent" />
        <NewsHotMatches />
        <NewsHighLight />
      </div>

      <div class="col-lg-9 news-content__right">
        <SeoHeadings />

        <template v-if="!categorySlugCurrent || categorySlugCurrent === sideBarCategories[0].slug">
          <div class="hot-news">
            <div v-if="dataNews[0]?.data" class="hot-news__content">
              <div class="hot-news__header">
                <div class="hot-news__filter form-filter__select">
                  <span class="text-title">Tin Hot</span>
                </div>
                <SliderNavigation />
              </div>
              <div
                :class="[
                  'hot-news__slider',
                  { 'blur-background-right': sliderState.isBlurredRight },
                  { 'blur-background-left': sliderState.isBlurredLeft }
                ]"
              >
                <Swiper
                  ref="newsSwiper"
                  :modules="modules"
                  :navigation="{
                    nextEl: '.hot-news .slider-navigation__next',
                    prevEl: '.hot-news .slider-navigation__prev'
                  }"
                  :slidesPerView="SLIDERS_PER_VIEW.SMALL"
                  :spaceBetween="SPACE_BETWEEN_SWIPER.XLARGE"
                >
                  <SwiperSlide v-for="(item, index) in dataNews[0]?.data" :key="index" class="news-item">
                    <div class="news-item__thumb hover-shine is-vertical">
                      <NuxtLink :to="'/' + item.slug" :title="item.title">
                        <img v-if="item.image" :alt="item.title" :src="item.image" loading="lazy" />
                      </NuxtLink>
                    </div>
                    <div class="news-item__content">
                      <NuxtLink :to="'/' + item.slug" :title="item.title" class="line-limit-2 news-item__title">
                        {{ item.title }}
                        <div class="news-item__desc line-limit-2" v-html="item.desc"></div>
                      </NuxtLink>
                    </div>
                    <div class="news-item__footer">
                      <div class="news-item__category-slug">
                        {{ dataNews[0].category.name }}
                      </div>
                      <div class="news-item__content-time">
                        <span class="icon-clock"></span>
                        <div class="date-time">
                          <div class="time">{{ item.time }}</div>
                          |
                          <div class="date">{{ item.date || item.publishTime }}</div>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                </Swiper>
              </div>
            </div>
          </div>

          <div id="weekly-news" class="weekly-news">
            <div class="weekly-news__content">
              <div class="weekly-news__header">
                <div class="weekly-news__filter form-filter__select">
                  <span class="text-title">Tin Trong Tuần</span>
                </div>
              </div>
              <NewsWeekly />
            </div>
          </div>
        </template>
        
        <template v-else>
          <NewsCategory :categorySlug="categorySlugCurrent" />
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '~scssAsset/desktop/pages/news/index.scss';
@import '~scssAsset/desktop/pages/news/news-page.scss';
</style>
